//deps
import React, { memo, useCallback, useMemo, useContext, useState, useEffect, useRef } from "react";

//internal deps
import {
    DineoutCartType,
    DineoutCheckoutRoute,
    DineoutCartContext,
    IGeneralRedemptionSectionMeta,
    getDineoutFlowType,
    closeWebview,
    getBridgeSdk,
    BLAZE_EVENTS,
    recordGenericErrorNREvent,
    CheckoutContext,
    CheckoutActionTypes,
} from "@swiggy-private/checkout-core";
import { CancellationPolicyWidget, GenericError, RouteProps, UpdateProgressLoader } from "@swiggy-private/core-ui";
import { SavingWidget, CartUIWrapper as StealStoreCartUIWrapper } from "@swiggy-private/dineout-cart-ui";
import { CartItemListRenderer } from "@swiggy-private/dls-web-recipe-do-web/dist/components/CartItemListRenderer";
import { Toast } from "@swiggy-private/dls-web-recipe-food-web/dist/components/Toast";
import { VoucherRedemptionBottomSheet } from "@swiggy-private/dls-web-recipe-do-web/dist/components/StealStore";
import { BillDetailsRenderer } from "@swiggy-private/dineout-cart-ui";

// hooks
import useFetchDineoutCart from "@dineout-cart-client/hooks/useFetchDineoutCart";
import useDineoutCollapsibleBillDetails from "@dineout-cart-client/hooks/useDineoutCollapsibleBillDetails";
import { useStealDealAnalytics } from "@dineout-cart-client/hooks/useStealDealAnalytics";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { getSavingWidgetData } from "@dineout-cart-client/utils/TableReservationUtils";
import { getCancellationWidget } from "@dineout-cart-client/helpers/cartHelper";
import {
    getCartItemList,
    getRedemptionMeta,
    getGroupCartRendererItems,
    processRedemptionMeta,
} from "@dineout-cart-client/helpers/StealDealCart";

// constants
import { CONTAINER_ID, STEAL_STORE_CART_ERROR_DATA_TEST_ID } from "./constants";
import { CART_GENERIC_EVENT_CASES } from "@dineout-cart-client/constants/Coupon";

//styles
import { GroupCartContainer, SavingWidgetWrapper } from "./styles";
import { ToastContainer } from "../table-reservation/styles";

// components
import CartFooter from "../cart-footer/CartFooter";

// interfaces
import { ITransformedGroupedItems } from "./types";

// contexts
import { UpdateCartContext } from "@dineout-cart-client/contexts";
import useDineoutCartItemActions from "@dineout-cart-client/hooks/useDineoutCartItemActions";
import useFetchOutletsValidityInfo from "@dineout-cart-client/hooks/useFetchOutletsValidityInfo";
import { recordCartPercievedTime } from "@dineout-cart-client/utils/Metric";
import { RECORD_PERCIEVED_TIME_EVENTS } from "@dineout-cart-client/constants/Blaze";
import { PaymentAlert } from "@swiggy-private/core-ui";

const StealStoreCartRenderer = (_props: RouteProps<DineoutCheckoutRoute>) => {
    const { cartState, uniqueIdForCart } = useContext(DineoutCartContext);
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { reviewedCart } = cartState;
    const cartResponse = useMemo(() => reviewedCart?.data, [reviewedCart]);

    const [isRedemptionOpen, setIsRedemptionOpen] = useState(false);
    const [redemptionData, setRedemptionData] = useState<{
        redemptionMeta: IGeneralRedemptionSectionMeta | null;
        brandName?: string;
        itemId?: string;
    } | null>(null);
    const [initialCartLoaded, setInitialCartLoaded] = useState(false);

    const hasHandledEmptyCartRef = useRef(false);
    const [cartFooterHeight, setCartFooterHeight] = useState(0);

    const [softError, setSoftError] = useState<string>();

    const { loading } = useFetchDineoutCart(DineoutCartType.STEAL_STORE_CART);

    const { updateCartLoading, updateCartError } = useContext(UpdateCartContext);

    const {
        onImpressionCancellationPolicyWidget,
        onimpressionSavingsBanner,
        onClickStealStoreCartBack,
        onClickTicketItem,
        onClickDeleteTicketItem,
        onImpressionTurboPayBtn,
        onImpressionEligibleOutletsForItems,
        onClickEligibleOutlets,
        onImpressionStealStoreCart,
        onClickTurboPaymentMethodChange,
        onImpressionTurboCheckout,
        onClickTurboCheckout,
    } = useStealDealAnalytics(cartResponse);

    const {
        onItemQuantityUpdate,
        onDeleteItem,
        onRemoveUnavailableItems,
        updatingItem,
        onClearCart,
        onRemoveExcessItems,
    } = useDineoutCartItemActions({
        cartType: DineoutCartType.STEAL_STORE_CART,
        onClickTicketItem,
        onClickDeleteTicketItem,
    });

    const { billDetailsProps } = useDineoutCollapsibleBillDetails();

    const { eligibleCities, isCityApiLoading, hasError, onCheckValidityClick } = useFetchOutletsValidityInfo(
        cartResponse?.cartId,
    );

    const savingWidgetData = useMemo(() => {
        return getSavingWidgetData(cartResponse?.widgets);
    }, [cartResponse]);

    const cancellationWidgetData = useMemo(() => {
        return getCancellationWidget(cartResponse?.widgets);
    }, [cartResponse]);

    const billToPay = useMemo(() => {
        return cartResponse?.bill?.dineoutPricingInfo?.billToPay;
    }, [cartResponse?.bill?.dineoutPricingInfo?.billToPay]);

    const { groupCartItems, totalItems } = useMemo(() => {
        return getGroupCartRendererItems(cartResponse);
    }, [cartResponse]);

    const redemptionMeta = useMemo(() => {
        return getRedemptionMeta(cartResponse);
    }, [cartResponse]);

    const handleItemClick = useCallback(
        (item) => {
            const processedRedemptionData: IGeneralRedemptionSectionMeta | null = processRedemptionMeta(
                redemptionMeta,
                item,
            );
            setRedemptionData({
                redemptionMeta: processedRedemptionData,
                brandName: item?.brandName,
                itemId: item?.itemId,
            });
            setIsRedemptionOpen(true);
            onClickEligibleOutlets(item?.eligibleOutlets, item?.itemId);
        },
        [redemptionMeta, setIsRedemptionOpen, setRedemptionData, onClickEligibleOutlets],
    );

    const handleCheckValidity = useCallback(() => {
        if (redemptionData?.itemId) {
            onCheckValidityClick(redemptionData.itemId);
        }
    }, [redemptionData?.itemId, onCheckValidityClick]);

    const getCartItemListData = useCallback(
        (groupCartItem: ITransformedGroupedItems) => {
            const items = groupCartItem?.items ?? [];
            return getCartItemList(items, { updatingItem }, groupCartItem?.brandName);
        },
        [updatingItem],
    );

    const handleRedemptionClose = useCallback(() => {
        setRedemptionData(null);
        setIsRedemptionOpen(false);
    }, [setIsRedemptionOpen, setRedemptionData]);

    const reloadCart = useCallback(() => {
        window.location.reload();
    }, []);

    const onErrorImpression = useCallback(() => {
        recordGenericErrorNREvent({
            errorContext: CART_GENERIC_EVENT_CASES.STEAL_DEAL_CART_ERROR,
        });
    }, []);

    const onDismissPaymentAlert = useCallback(() => {
        setCheckoutState({ type: CheckoutActionTypes.STEAL_STORE_CART });
    }, [setCheckoutState]);

    useEffect(() => {
        setSoftError(updateCartError?.errorPayload?.error?.statusMessage);
    }, [updateCartError?.errorPayload?.error?.statusMessage]);

    useEffect(() => {
        // if cart is empty then redirect user to the menu page (only after we have response)
        if (
            !loading &&
            !isEmpty(cartResponse) &&
            cartResponse &&
            isEmpty(cartResponse.groupedItems) &&
            !hasHandledEmptyCartRef.current
        ) {
            hasHandledEmptyCartRef.current = true;
            const clearAndClose = async () => {
                try {
                    await getBridgeSdk().dineoutClearLocalCart({
                        flowType: getDineoutFlowType(DineoutCartType.STEAL_STORE_CART),
                    });
                } catch {
                    // no-op
                }
                closeWebview();
            };
            clearAndClose();
        }
    }, [loading, cartResponse]);

    useEffect(() => {
        onImpressionStealStoreCart();
    }, [onImpressionStealStoreCart]);

    useEffect(() => {
        groupCartItems?.forEach((groupCartItem) => {
            onImpressionEligibleOutletsForItems(groupCartItem?.items ?? []);
        });
    }, [groupCartItems, onImpressionEligibleOutletsForItems]);

    useEffect(() => {
        if (loading || initialCartLoaded || isEmpty(reviewedCart)) {
            return;
        }

        recordCartPercievedTime(
            BLAZE_EVENTS.RECORD_STEAL_STORE_CART_LOAD_TIME,
            RECORD_PERCIEVED_TIME_EVENTS.STEAL_STORE_APP_LOAD_TIME,
        );
        setInitialCartLoaded(true);
    }, [loading, initialCartLoaded, reviewedCart]);

    // First priority: loading shimmer, then error (header always visible via wrapper)
    const isLoading = loading && isEmpty(cartResponse);
    const isCartError = !loading && isEmpty(cartResponse);

    if (isCartError) {
        return (
            <GenericError
                action={{ onClick: reloadCart }}
                pageImpressionEvent={onErrorImpression}
                dataTestId={STEAL_STORE_CART_ERROR_DATA_TEST_ID}
            />
        );
    }

    const isEmptyCartRedirect = !loading && !isEmpty(cartResponse) && isEmpty(cartResponse?.groupedItems);
    if (isEmptyCartRedirect) {
        return <></>;
    }

    return (
        <>
            {updateCartLoading && <UpdateProgressLoader />}
            <StealStoreCartUIWrapper
                containerId={CONTAINER_ID}
                totalItemsCount={totalItems ?? 0}
                isLoading={isLoading}
                onBack={onClickStealStoreCartBack}
            >
                {savingWidgetData && (
                    <SavingWidgetWrapper>
                        <SavingWidget props={savingWidgetData} onImpression={onimpressionSavingsBanner} />
                    </SavingWidgetWrapper>
                )}

                <GroupCartContainer>
                    {groupCartItems?.map((groupCartItem, index) => (
                        <CartItemListRenderer
                            key={index}
                            title={groupCartItem.brandName || ""}
                            itemCountTitle={`${groupCartItem?.items?.length || 0} items`}
                            itemList={getCartItemListData(groupCartItem)}
                            showAddButton={true}
                            updateItemQuantity={onItemQuantityUpdate}
                            onItemClick={handleItemClick}
                            onRemoveItem={onDeleteItem}
                            enableSoldOutComs={false}
                            disableBoxShadow={false}
                            itemsSpacing="medium"
                            isLeftItemDisabled={true}
                        />
                    ))}
                </GroupCartContainer>

                <BillDetailsRenderer {...billDetailsProps} />
                <CancellationPolicyWidget
                    cancellationDisclaimerData={cancellationWidgetData?.widgetInfo?.cancellationPolicyWidget}
                    onViewEnter={onImpressionCancellationPolicyWidget}
                />

                <VoucherRedemptionBottomSheet
                    isOpen={isRedemptionOpen}
                    onClose={handleRedemptionClose}
                    redemptionMeta={redemptionData?.redemptionMeta || null}
                    brandName={redemptionData?.brandName}
                    eligibleCities={eligibleCities}
                    onCheckValidity={handleCheckValidity}
                    hasError={hasError}
                    isLoading={isCityApiLoading}
                />

                {!isEmpty(softError) && (
                    <ToastContainer bottomOffset={cartFooterHeight}>
                        <Toast message={softError || ""} containerClass={"toast"} show autoDismiss />
                    </ToastContainer>
                )}
                <CartFooter
                    uniqueIdForCart={uniqueIdForCart}
                    billToPay={billToPay}
                    flowtype={getDineoutFlowType(DineoutCartType.STEAL_STORE_CART)}
                    onRemoveUnavailableItems={onRemoveUnavailableItems}
                    onRemoveExcessItems={onRemoveExcessItems}
                    onClearCart={onClearCart}
                    onDismissPaymentAlert={onDismissPaymentAlert}
                    onHeightChange={setCartFooterHeight}
                    analytics={{
                        onClickTurboPaymentMethodChange,
                        onImpressionTurboCheckout,
                        onClickTurboCheckout,
                        onImpressionTurboPayBtn,
                    }}
                />

                {checkoutState.type === CheckoutActionTypes.GENERIC_PAYMENT_ALERT && (
                    <PaymentAlert {...checkoutState.payload} />
                )}
            </StealStoreCartUIWrapper>
        </>
    );
};
export default memo(StealStoreCartRenderer);
